import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL } from "../api";
import MobileHeader from "../mobile/MobileHeader";
import MobileBottomNav from "../mobile/MobileBottomNav";
import { COLOR, FONT, RADIUS, ThemeProvider, GlobalMobileStyles } from "../mobile/mobiletheme";
import shiperboxLogo from "../assets/shiperbox-logo.png";
import { getImageUrl } from "../utils/imageUrl";
import Login from "../pages/Login";

export default function MobileItemsList() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [items, setItems] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedSub, setSelectedSub] = useState("All");
  const [headerHeight, setHeaderHeight] = useState(112);
  const [showLogin, setShowLogin] = useState(false);
  const [brokenImages, setBrokenImages] = useState({});

  const getUserId = () => {
    try { return localStorage.getItem("phone") || "guest"; } catch (_) { return "guest"; }
  };
  const user_id = getUserId();
  const isLoggedIn = () => {
    try { return localStorage.getItem("isLoggedIn") === "true"; } catch (_) { return false; }
  };

  const getName = (product) => {
    let name = product?.name;
    if (typeof name === "string") { try { const parsed = JSON.parse(name); if (parsed && typeof parsed === "object") name = parsed; } catch (_) {} }
    if (typeof name === "string") return name;
    const lang = (i18n.language || "en").split("-")[0];
    return name?.[lang] || name?.en || name?.hi || name?.te || "";
  };

  const cartFetchSeq = useRef(0);

  const fetchCartData = useCallback(async () => {
    if (user_id === "guest") return;
    const seq = ++cartFetchSeq.current;
    try {
      const res = await axios.get(`${API_URL}/cart/${user_id}`);
      if (seq !== cartFetchSeq.current) return;
      if (res.data.success) setCartItems(res.data.cart);
    } catch (err) {
      console.log("Cart fetch error:", err);
    }
  }, [user_id]);

  const fetchItems = useCallback(() => {
    setLoading(true);
    setError(false);
    axios.get(`${API_URL}/items/${categoryId}`)
      .then((res) => {
        if (res.data.success) setItems(res.data.items);
        else setError(true);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [categoryId]);

  useEffect(() => {
    fetchCartData();
    fetchItems();
  }, [categoryId, fetchCartData, fetchItems]);

  useEffect(() => {
    setBrokenImages({});
  }, [categoryId]);

  const isFlowers = categoryId === "flowers";

  const subCategories = useMemo(
    () => (isFlowers ? [] : ["All", ...new Set(items.map((i) => i.subcategory || "General"))]),
    [items, isFlowers]
  );

  useEffect(() => {
    setSelectedSub((prev) => (isFlowers ? "All" : (subCategories.includes(prev) ? prev : "All")));
  }, [categoryId, subCategories, isFlowers]);

  const filteredItems = useMemo(() => {
    const base = (selectedSub === "All" || isFlowers) ? items : items.filter((i) => (i.subcategory || "General") === selectedSub);
    // Push out-of-stock items to the bottom of the grid, without disturbing
    // the relative order of the in-stock items (or of the out-of-stock
    // items among themselves) - Array.prototype.sort is stable, so a
    // 0/1 comparator here is enough to just partition the two groups.
    return [...base].sort((a, b) => {
      const aOut = Number(a.stock) <= 0 ? 1 : 0;
      const bOut = Number(b.stock) <= 0 ? 1 : 0;
      return aOut - bOut;
    });
  }, [items, selectedSub, isFlowers]);

  const getSubcategoryImage = (subName) => {
    if (subName === "All" && items.length > 0) return getImageUrl(items[0].image);
    const item = items.find((i) => (i.subcategory || "General").toLowerCase() === subName.toLowerCase());
    return item ? getImageUrl(item.image) : "";
  };

  const goSearch = () => {
    if (search.trim() !== "") navigate(`/search?q=${encodeURIComponent(search.trim())}`);
  };

  const formatTitle = (str) =>
    str ? str.replace(/-/g, " ").toUpperCase() : "";

  const [pendingIds, setPendingIds] = useState({});

  const addToCart = async (product) => {
    if (!isLoggedIn() || user_id === "guest") {
      setShowLogin(true);
      return;
    }
    const pendingKey = `add-${product.id}`;
    if (pendingIds[pendingKey]) return; 
    setPendingIds((p) => ({ ...p, [pendingKey]: true }));

    const tempId = `temp-${product.id}`;
    setCartItems((prev) => [...prev, { id: tempId, product_id: product.id, category: categoryId, quantity: 1 }]);
    window.dispatchEvent(new Event("cartUpdated"));

    try {
      const res = await axios.post(`${API_URL}/cart/add`, { user_id, product_id: product.id, category: categoryId });
      const real = res.data?.cartItem;
      setCartItems((prev) => prev.map((i) => (i.id === tempId
        ? { ...i, id: real ? real.id : i.id, quantity: real ? real.quantity : i.quantity }
        : i)));
    } catch (err) {
      setCartItems((prev) => prev.filter((i) => i.id !== tempId));
      alert(t("mobile.tryAgain", { defaultValue: "Couldn't add item — please try again." }));
    } finally {
      window.dispatchEvent(new Event("cartUpdated"));
      setPendingIds((p) => {
        const next = { ...p };
        delete next[pendingKey];
        return next;
      });
    }
  };

  const updateQty = async (cart_id, action) => {
    if (typeof cart_id === "string" && cart_id.startsWith("temp-")) return; 
    if (pendingIds[cart_id]) return; 
    const item = cartItems.find((i) => i.id === cart_id);
    if (!item) return;
    const prevSnapshot = cartItems;
    setPendingIds((p) => ({ ...p, [cart_id]: true }));

    if (action === "decrease" && item.quantity <= 1) {
      setCartItems((prev) => prev.filter((i) => i.id !== cart_id));
    } else {
      setCartItems((prev) => prev.map((i) => i.id === cart_id
        ? { ...i, quantity: action === "increase" ? i.quantity + 1 : i.quantity - 1 }
        : i));
    }
    window.dispatchEvent(new Event("cartUpdated"));

    try {
      if (action === "decrease" && item.quantity <= 1) {
        await axios.delete(`${API_URL}/cart/${cart_id}`);
      } else {
        await axios.put(`${API_URL}/cart/${action}/${cart_id}`);
      }
    } catch (err) {
      setCartItems(prevSnapshot);
    } finally {
      window.dispatchEvent(new Event("cartUpdated"));
      setPendingIds((p) => {
        const next = { ...p };
        delete next[cart_id];
        return next;
      });
    }
  };

  const bottomReserved = `env(safe-area-inset-bottom, 0px)`;

  return (
    <ThemeProvider>
      <GlobalMobileStyles />
      <div style={styles.container}>
      {showLogin && (
        <div style={styles.overlay} className="mil-overlay">
          <div style={styles.loginBox} className="mil-login-box">
            <button style={styles.closeBtn} onClick={() => setShowLogin(false)} aria-label={t("mobile.closeLogin")}>×</button>
            <Login
              onLoginSuccess={() => {
                setShowLogin(false);
                fetchCartData();
              }}
            />
          </div>
        </div>
      )}

      <MobileHeader
        searchValue={search}
        setSearchValue={setSearch}
        onSearch={goSearch}
        showLogo={false}
        showTitleBar
        showBackButton={true}
        onBack={() => navigate(-1)}
        title={t(`categories.${categoryId}`, { defaultValue: formatTitle(categoryId) })}
        activeTab="category"
        isLoggedIn={isLoggedIn()}
        onLoginClick={() => setShowLogin(true)}
        onHeightChange={setHeaderHeight}
      />

      <div
        style={{
          ...styles.mainWrapper,
          top: `${headerHeight}px`,
          bottom: bottomReserved,
        }}
      >
        {!isFlowers && (
          <div style={styles.sidebar}>
            {subCategories.map((sub) => {
              const subImage = getSubcategoryImage(sub);
              const active = selectedSub === sub;
              const subLabel = sub === "All" ? t("mobile.showAll", { defaultValue: "All" }) : sub;
              return (
                <div
                  key={sub}
                  style={styles.sideItem}
                  className="mil-side-item mil-cat-label"
                  onClick={() => setSelectedSub(sub)}
                  role="button"
                  tabIndex={0}
                  aria-pressed={active}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedSub(sub); } }}
                >
                  <div
                    className="mil-cat-circle"
                    style={{ ...styles.catCircle, border: active ? `2px solid ${COLOR.primary}` : "2px solid transparent", transform: active ? "scale(1.05)" : "scale(1)" }}
                  >
                    {subImage ? (
                      <img src={subImage} alt={subLabel} style={styles.catImage} loading="lazy" />
                    ) : (
                      <div style={styles.catPlaceholder} />
                    )}
                  </div>
                  <span style={active ? styles.catLabelActive : styles.catLabel}>{subLabel}</span>
                </div>
              );
            })}
          </div>
        )}

        <div style={styles.scrollArea}>
          {loading ? (
            <div style={styles.grid}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={styles.skeletonCard}>
                  <div style={styles.skeletonImage} />
                  <div style={styles.skeletonLine} />
                  <div style={{ ...styles.skeletonLine, width: "50%" }} />
                </div>
              ))}
            </div>
          ) : error ? (
            <div style={styles.statusWrap} className="mil-fade-in">
              <div style={styles.statusIcon} aria-hidden="true">⚠️</div>
              <p style={styles.statusText}> {t("mobile.couldntLoadItems")} </p>
              <button style={styles.retryBtn} className="mil-press" onClick={fetchItems}> {t("mobile.tryAgain")} </button>
            </div>
          ) : filteredItems.length === 0 ? (
            <div style={styles.statusWrap} className="mil-fade-in">
              <div style={styles.statusIcon} aria-hidden="true">🔍</div>
              <p style={styles.statusText}>{t("mobile.noProducts")}</p>
              {selectedSub !== "All" && (
                <button style={styles.retryBtn} className="mil-press" onClick={() => setSelectedSub("All")}> {t("mobile.showAll")} </button>
              )}
            </div>
          ) : (
            <div style={styles.grid}>
              {filteredItems.map((product, idx) => {
                const cartItem = cartItems.find((c) => c.product_id === product.id && c.category === categoryId);
                const stockNum = Number(product.stock);
                const outOfStock = stockNum <= 0;
                const lowStock = !outOfStock && stockNum <= 5;
                const atStockLimit = !!cartItem && stockNum > 0 && cartItem.quantity >= stockNum;
                const addPending = !!pendingIds[`add-${product.id}`];
                const imgBroken = !!brokenImages[product.id];
                const displayName = getName(product);
                
                return (
                  <div
                    key={product.id}
                    className="mil-card"
                    style={{
                      ...(outOfStock ? { ...styles.card, ...styles.cardOutOfStock } : styles.card),
                      animationDelay: `${Math.min(idx, 9) * 30}ms`,
                    }}
                  >
                    <div style={styles.imgWrapper}>
                      {imgBroken ? (
                        <div style={styles.imgFallback} aria-hidden="true">🖼️</div>
                      ) : (
                        <img
                          src={getImageUrl(product.image)}
                          style={outOfStock ? { ...styles.image, ...styles.imageOutOfStock } : styles.image}
                          alt={displayName}
                          loading="lazy"
                          onError={() => setBrokenImages((p) => ({ ...p, [product.id]: true }))}
                        />
                      )}
                      {outOfStock && <div style={styles.outOfStockBadge}> {t("mobile.outOfStock")} </div>}
                      {lowStock && <div style={styles.lowStockBadge}>{stockNum === 1 ? t("mobile.left", { count: 1 }) : t("mobile.left", { count: stockNum })}</div>}
                    </div>

                    <h4 style={styles.gridTitle}>{displayName}</h4>
                    <span style={styles.gridUnit}>{product.quantity || "1 kg"}</span>

                    <div style={styles.priceRow}>
                      <p style={styles.price}>₹{product.price}</p>

                      {outOfStock ? (
                        <span style={styles.outOfStockText}> {t("mobile.outOfStock")} </span>
                      ) : cartItem ? (
                        <div style={styles.qtyBox} className="mil-qty-box">
                          <button
                            style={styles.btn}
                            className="mil-press"
                            disabled={!!pendingIds[cartItem.id] || addPending}
                            onClick={() => updateQty(cartItem.id, "decrease")}
                            aria-label={`Decrease quantity of ${displayName}`}
                          >−</button>
                          <span
                            key={`${cartItem.id}-${cartItem.quantity}`}
                            style={styles.qty}
                            className="mil-qty-pulse"
                            aria-live="polite"
                          >{cartItem.quantity}</span>
                          <button
                            style={{ ...styles.btn, ...((atStockLimit || addPending) ? styles.btnDisabled : {}) }}
                            className="mil-press"
                            disabled={!!pendingIds[cartItem.id] || addPending || atStockLimit}
                            title={atStockLimit ? t("mobile.onlyInStock", { count: stockNum }) : undefined}
                            onClick={() => updateQty(cartItem.id, "increase")}
                            aria-label={`Increase quantity of ${displayName}`}
                          >+</button>
                        </div>
                      ) : (
                        <button
                          style={styles.addBtn}
                          className="mil-press"
                          disabled={!!pendingIds[`add-${product.id}`]}
                          onClick={() => addToCart(product)}
                          aria-label={`Add ${displayName} to cart`}
                        >
                          {pendingIds[`add-${product.id}`] ? "..." : t("mobile.add")}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={styles.brandFooter}>
            <div style={styles.brandDivider} />
            <div style={styles.brandLogoRow}>
              <img src={shiperboxLogo} alt="Shiperbox" style={styles.brandLogoImg} />
            </div>
          </div>
        </div>
      </div>

      <MobileBottomNav viewCartBar />
      </div>
    </ThemeProvider>
  );
}

const BRAND = COLOR.primary;

const styles = {
  container: {
    position: "fixed",
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "transparent",
    overflow: "hidden"
  },
  mainWrapper: {
    position: "absolute",
    left: 0, right: 0,
    display: "flex",
    boxSizing: "border-box",
    overflow: "hidden",
    backgroundColor: COLOR.mist,
  },
  scrollArea: {
    flex: 1,
    height: "100%",
    minWidth: 0,
    padding: "15px",
    paddingBottom: "calc(90px + env(safe-area-inset-bottom, 0px))",
    boxSizing: "border-box",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  },
  sidebar: {
    width: "clamp(68px, 21vw, 85px)",
    height: "100%",
    backgroundColor: COLOR.paper,
    borderRight: `1px solid ${COLOR.line}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px 0",
    paddingBottom: "calc(90px + env(safe-area-inset-bottom, 0px))",
    overflowY: "auto",
    boxSizing: "border-box",
  },
  sideItem: { marginBottom: "20px", textAlign: "center", cursor: "pointer", width: "100%" },
  catCircle: { width: "62%", aspectRatio: "1 / 1", borderRadius: "50%", backgroundColor: COLOR.mist, margin: "0 auto 6px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", boxSizing: "border-box" },
  catImage: { width: "100%", height: "100%", objectFit: "cover" },
  catPlaceholder: { width: "100%", height: "100%", backgroundColor: COLOR.mist },
  catLabel: { fontSize: "11px", color: COLOR.muted, fontWeight: "600", display: "block", lineHeight: 1.3 },
  catLabelActive: { fontSize: "11px", color: COLOR.primary, fontWeight: "700", display: "block", lineHeight: 1.3 },

  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "22px 14px" },
  card: { backgroundColor: "transparent", padding: 0, borderRadius: 0, boxShadow: "none", display: "flex", flexDirection: "column", minWidth: 0, overflow: "visible" },
  imgWrapper: { width: "100%", aspectRatio: "1.0", overflow: "hidden", borderRadius: "12px", marginBottom: "8px", backgroundColor: COLOR.mist, position: "relative" },
  image: { width: "100%", height: "100%", objectFit: "cover" },
  imgFallback: { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", backgroundColor: COLOR.mist, color: COLOR.muted },
  cardOutOfStock: { opacity: 0.6 },
  imageOutOfStock: { filter: "grayscale(1)" },
  outOfStockBadge: { position: "absolute", top: "6px", left: "6px", backgroundColor: COLOR.ink, color: COLOR.onPrimary, fontSize: "9px", fontWeight: "bold", padding: "3px 6px", borderRadius: "5px", textTransform: "uppercase", letterSpacing: "0.2px" },
  outOfStockText: { fontSize: "11px", fontWeight: "bold", color: COLOR.error },
  lowStockBadge: { position: "absolute", top: "6px", left: "6px", backgroundColor: COLOR.pop, color: COLOR.onPrimary, fontSize: "9px", fontWeight: "bold", padding: "3px 6px", borderRadius: "5px", letterSpacing: "0.2px" },
  btnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  gridTitle: { fontSize: "13.5px", fontWeight: "600", color: COLOR.ink, margin: "0 0 3px 0", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35, overflow: "visible", textOverflow: "clip" },
  gridUnit: { fontSize: "12px", color: COLOR.muted, margin: "0 0 10px 0", fontWeight: "500", display: "block" },
  priceRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto" },
  price: { fontWeight: "900", fontSize: "13px", color: COLOR.ink, margin: 0 },
  addBtn: { margin: "0 0 2px", padding: "3px 10px", backgroundColor: COLOR.paper, color: COLOR.primary, border: `1.5px solid ${COLOR.primary}`, borderRadius: "5px", fontWeight: "700", fontSize: "13px", letterSpacing: "0.02em", textTransform: "uppercase", cursor: "pointer" },
  qtyBox: { margin: "0 0 4px 7px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", backgroundColor: COLOR.primary, border: `1.5px solid ${COLOR.primary}`, borderRadius: "5px", padding: "1px 3px" },
  btn: { border: "none", background: "none", color: COLOR.onPrimary, fontWeight: "bold", cursor: "pointer", fontSize: "18px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" },
  qty: { fontSize: "16px", fontWeight: "700", color: COLOR.onPrimary, minWidth: "14px", textAlign: "center", display: "inline-block" },

  statusWrap: { textAlign: "center", padding: "30px", width: "100%" },
  statusIcon: { fontSize: "32px", marginBottom: "8px" },
  statusText: { textAlign: "center", color: COLOR.muted, padding: "0 0 12px", width: "100%", margin: 0 },
  retryBtn: { border: `1.5px solid ${COLOR.primary}`, background: COLOR.primarySoft, color: COLOR.primary, borderRadius: "8px", padding: "8px 18px", fontWeight: "700", fontSize: "13px", cursor: "pointer" },

  skeletonCard: { backgroundColor: "transparent", padding: 0 },
  skeletonImage: { width: "100%", aspectRatio: "1.0", borderRadius: "12px", marginBottom: "8px", background: `linear-gradient(90deg, ${COLOR.line} 25%, ${COLOR.mist} 37%, ${COLOR.line} 63%)`, backgroundSize: "400% 100%", animation: "itemsSkeleton 1.4s ease infinite" },
  skeletonLine: { height: "12px", borderRadius: "6px", marginBottom: "8px", width: "80%", background: `linear-gradient(90deg, ${COLOR.line} 25%, ${COLOR.mist} 37%, ${COLOR.line} 63%)`, backgroundSize: "400% 100%", animation: "itemsSkeleton 1.4s ease infinite" },

  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: COLOR.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 },
  loginBox: { background: COLOR.paper, padding: "20px", borderRadius: "12px", width: "90%", maxWidth: "360px", position: "relative", boxSizing: "border-box" },
  closeBtn: { position: "absolute", top: "10px", right: "10px", border: "none", background: "none", fontSize: "24px", cursor: "pointer", color: COLOR.ink, zIndex: 10 },

  brandFooter: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "100px 24px 12px" },
  brandDivider: { width: "100px", height: "3px", background: `linear-gradient(90deg, ${COLOR.primary}55, ${COLOR.primaryDark}55)`, marginBottom: "14px", borderRadius: RADIUS.pill },
  brandLogoRow: { display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 0.9, flexWrap: "wrap" },
  brandLogoImg: { height: "clamp(24px, 7vw, 32px)", width: "auto", objectFit: "contain" },
};

if (typeof document !== "undefined" && !document.getElementById("items-skeleton-kf")) {
  const styleTag = document.createElement("style");
  styleTag.id = "items-skeleton-kf";
  styleTag.textContent = `
    @keyframes itemsSkeleton { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }

    @keyframes mil-fadeInUp {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes mil-pulse {
      0% { transform: scale(1); }
      40% { transform: scale(1.28); }
      100% { transform: scale(1); }
    }
    @keyframes mil-fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes mil-popIn {
      from { opacity: 0; transform: scale(0.92); }
      to { opacity: 1; transform: scale(1); }
    }

    .mil-card { animation: mil-fadeInUp 0.32s ease both; transition: transform 0.15s ease, box-shadow 0.15s ease; }
    .mil-card:active { transform: scale(0.98); }

    .mil-press { transition: transform 0.12s ease, filter 0.12s ease; }
    .mil-press:active:not(:disabled) { transform: scale(0.9); filter: brightness(0.96); }
    .mil-press:disabled { cursor: not-allowed; }

    .mil-qty-box { transition: background-color 0.2s ease; }
    .mil-qty-pulse { animation: mil-pulse 0.26s ease; }

    .mil-side-item { transition: transform 0.15s ease; }
    .mil-side-item:active { transform: scale(0.95); }
    .mil-cat-circle { transition: transform 0.2s ease, border-color 0.2s ease; }

    .mil-fade-in { animation: mil-fadeIn 0.3s ease both; }
    .mil-overlay { animation: mil-fadeIn 0.2s ease both; }
    .mil-login-box { animation: mil-popIn 0.22s ease both; }

    button:focus-visible, [role="button"]:focus-visible {
      outline: 2px solid var(--primary);
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      .mil-card, .mil-press, .mil-qty-pulse, .mil-side-item, .mil-cat-circle, .mil-fade-in, .mil-overlay, .mil-login-box {
        animation: none !important;
        transition: none !important;
      }
    }
  `;
  document.head.appendChild(styleTag);
}